import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import athleteService from '../../services/athletes';
import styles from './styles.module.scss';
import history from 'shared_redux/history';
import EmailMismatchMessage from './EmailMismatchMessage';
import InviteAcceptedSuccess from './InviteAcceptedSuccess';

/**
 * AthleteInvite component for handling athlete invitations
 */
const AthleteInvite = () => {
    const { inviteCode } = useParams();
    const [loading, setLoading] = useState(true);
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState(null);
    const [isAuthError, setIsAuthError] = useState(false);
    const [emailMismatch, setEmailMismatch] = useState(null);
    const [inviteAccepted, setInviteAccepted] = useState(false);
    const [acceptedData, setAcceptedData] = useState(null);
    const [alreadyAccepted, setAlreadyAccepted] = useState(false);
    
    // Get the email from URL query parameters
    const [inviteEmail, setInviteEmail] = useState('');
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const emailParam = queryParams.get('email');
        if (emailParam) {
            setInviteEmail(emailParam);
        }
    }, []);

    useEffect(() => {
        console.log('Fetching invite with code:', inviteCode);

        setLoading(true);
        // Reset states
        setIsAuthError(false);
        setEmailMismatch(null);
        setError(null);
        setInvite(null);

        // Use the service with promise chaining instead of async/await
        athleteService.getAthleteInviteByCode(inviteCode)
            .then(response => {
                console.log('Success response:', response);

                // Check if this is an already accepted invite response
                if (response.isAlreadyAccepted) {
                    setAlreadyAccepted(true);
                    setAcceptedData({
                        athlete: response.athlete,
                        organization: response.organization,
                        organization_details: {
                            name: response.organization.name,
                            logo_url: response.organization.logo_url
                        }
                    });
                } else {
                    // Check if user is authenticated based on the response
                    if (response.user_status && !response.user_status.is_authenticated) {
                        console.log('User is not authenticated');
                        setIsAuthError(true);
                    } else {
                        console.log('User is authenticated, showing invite details');
                        setInvite(response);
                    }
                }

                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching invite:', err);

                // Check if it's an authentication error
                if (isAuthenticationError(err)) {
                    setIsAuthError(true);
                } else if (isEmailMismatchError(err)) {
                    const { currentEmail } = extractEmailsFromError(err);
                    setEmailMismatch({ currentEmail });
                } else {
                    setError('Unable to load invitation details. The invitation may have expired or been revoked.');
                }
                setLoading(false);
            });
    }, [inviteCode]);


    // Show success page if invite was accepted just now or was already accepted
    if ((inviteAccepted || alreadyAccepted) && acceptedData) {
        return <InviteAcceptedSuccess 
            athlete={acceptedData.athlete} 
            organization={acceptedData.organization}
            organization_details={acceptedData.organization_details}
        />;
    }

    // Handle email mismatch error - show a dedicated UI component
    if (emailMismatch) {
        return <EmailMismatchMessage
            currentEmail={emailMismatch.currentEmail}
        />;
    }

    // Authentication error UI - when user is not logged in
    if (isAuthError) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h2>Authentication Required</h2>
                    <p>You need to be logged in to view this invitation.</p>
                    <div className={styles.buttonContainer}>
                        <Link
                            to={`/athleteSignup?athlete_invite=${inviteCode}${inviteEmail ? `&email=${encodeURIComponent(inviteEmail)}` : ''}`}
                            className={`${styles.button} ${styles.signupButton}`}
                        >
                            Create Account
                        </Link>
                        <Link
                            to={`/login?redirect=${encodeURIComponent(`/athlete-invite/${inviteCode}?email=${inviteEmail}`)}&email=${encodeURIComponent(inviteEmail)}&athlete_invite=${inviteCode}`}
                            className={styles.button}
                        >
                            Sign In
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={styles.spinner}></div>
                    <p>Loading invitation details...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h2>Invitation Error</h2>
                    <div className={styles.error}>{error}</div>
                    <button
                        className={styles.button}
                        onClick={() => history.push('/login')}
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    // Invite found - show invitation details with rich information
    if (invite) {
        // Use the data structure returned from the server
        const athleteName = invite.athlete?.name || '';
        const organization = invite.organization?.name || '';
        const logoUrl = invite.organization?.logo_url || null;

        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    {logoUrl && (
                        <div className={styles.logoContainer}>
                            <img src={logoUrl} alt="Organization logo" className={styles.logo} />
                        </div>
                    )}

                    <h2>Athlete Invitation</h2>

                    <div className={styles.inviteDetails}>
                        <p><strong>You've been invited to claim this athlete profile:</strong></p>
                        {athleteName && <p className={styles.athleteName}>{athleteName}</p>}

                        {organization && <p><strong>Organization:</strong> {organization}</p>}

                        {invite.expires_at && (
                            <p className={styles.note}>
                                Invitation expires: {new Date(invite.expires_at).toLocaleDateString()}
                            </p>
                        )}
                    </div>

                    <button
                        className={styles.button}
                        onClick={() => {
                            setLoading(true);
                            athleteService.acceptAthleteInvite(inviteCode)
                                .then((response) => {
                                    console.log('Invitation accepted successfully:', response);
                                    setAcceptedData({
                                        athlete: response.athlete,
                                        organization: response.organization,
                                        organization_details: response.organization_details
                                    });
                                    setInviteAccepted(true);
                                    setLoading(false);
                                })
                                .catch(err => {
                                    console.error('Error accepting invitation:', err);
                                    setError(err?.message || 'Failed to accept invitation');
                                    setLoading(false);
                                });
                        }}
                    >
                        {loading ? 'Processing...' : 'Accept Invitation'}
                    </button>
                </div>
            </div>
        );
    }

    // Fallback - shouldn't hit this normally
    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <h2>Invalid Invitation</h2>
                <p>Sorry, this invitation is no longer valid.</p>
                <button
                    className={styles.button}
                    onClick={() => history.push('/login')}
                >
                    Go to Login
                </button>
            </div>
        </div>
    );
};

// Helper functions for error handling

/**
 * Check if the error is an email mismatch error
 */
function isEmailMismatchError(err) {
  // Check for specific error code in the response data
  return (err.json && err.json.errorCode === 'EMAIL_MISMATCH');
}

/**
 * Check if the error is an authentication error
 */
function isAuthenticationError(err) {
  // Check for 401 status code - this is the main case
  if (err.statusCode === 401) {
    return true;
  }

  // Check for authentication keywords in specific error fields
  const errorMessage = (err.message || err.json?.message || '').toLowerCase();
  if (errorMessage.includes('unauthorized') || errorMessage.includes('not authenticated')) {
    return true;
  }

  return false;
}

/**
 * Extract emails from error response
 */
function extractEmailsFromError(err) {
  // Extract email from the error response
  let currentEmail = '';
  
  // Get from the json property containing the parsed response body
  if (err.json && err.json.currentUserEmail) {
    currentEmail = err.json.currentUserEmail;
  }
  
  return { currentEmail };
}

export default AthleteInvite;
